/**
 * Local mock of the edit-chat backend, for building the UI before the real
 * endpoint is deployed. Same interface as the service functions
 * (startEditChat / createEditChatSSE / getEditChatTurn / getEditChatHistory).
 *
 * Enabled by NEXT_PUBLIC_EDIT_CHAT_MOCK=1, or — outside production builds —
 * by opening the page with ?chatmock=1. A message ending in "?" is answered as
 * a free question; anything else produces steps, a "revision" that points at
 * the SAME job (so the real preview simply reloads) and a charge from the
 * mocked edit pack.
 */

const turns = new Map();
const histories = new Map();
const packs = new Map(); // jobId → { left, nextCost }
const PACK = 10;

export function isEditChatMockEnabled() {
  if (process.env.NEXT_PUBLIC_EDIT_CHAT_MOCK === '1') return true;
  if (process.env.NODE_ENV === 'production' || typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('chatmock') === '1';
  } catch {
    return false;
  }
}

const packFor = (jobId) => {
  if (!packs.has(jobId)) packs.set(jobId, { left: 3, nextCost: 0 });
  return packs.get(jobId);
};

const allowance = (pack) => ({
  edits_left_in_pack: pack.left, next_change_costs: pack.nextCost, included_edits: PACK,
});

export async function startEditChat(jobId, message) {
  if (/^\s*fail 402/i.test(message)) {
    const err = new Error('Not enough credits');
    err.statusCode = 402;
    err.error = 'INSUFFICIENT_CREDITS';
    throw err;
  }
  const turnId = `mock-${Date.now().toString(36)}`;
  const question = /\?\s*$/.test(message);
  turns.set(turnId, { jobId, message, question });
  return {
    turn_id: turnId,
    job_id: jobId,
    estimate_seconds: { typical: question ? 4 : 9, usual_max: 20, max: 60 },
    ...allowance(packFor(jobId)),
  };
}

export function createEditChatSSE(turnId) {
  const timers = [];
  const stream = {
    onEvent: null,
    onBroken: null,
    close() { timers.forEach(clearTimeout); },
  };
  const turn = turns.get(turnId);
  const at = (ms, event, data) => timers.push(setTimeout(() => stream.onEvent?.(event, data), ms));
  if (!turn) {
    at(50, 'error', { code: 'NOT_FOUND', message: 'Turn not found' });
    return stream;
  }
  const pack = packFor(turn.jobId);
  const log = histories.get(turn.jobId) || [];
  const now = Math.floor(Date.now() / 1000);
  log.push({
    role: 'user', text: turn.message, job_id_after: null, created_at: now,
  });
  if (turn.question) {
    at(500, 'step', { text: 'Reading the widget' });
    at(1500, 'step', { text: 'Listing its properties' });
    const answer = 'It has the properties shown in the panel on the left. Ask me to add, rename or bind one.';
    at(2500, 'message', { text: answer });
    at(2700, 'done', { credits_charged: 0, changed: false, ...allowance(pack) });
    log.push({
      role: 'assistant', text: answer, job_id_after: null, created_at: now,
    });
  } else {
    const charged = pack.nextCost;
    if (pack.nextCost > 0) { pack.left = PACK; pack.nextCost = 0; }
    pack.left -= 1;
    if (pack.left <= 0) { pack.left = 0; pack.nextCost = 1; }
    at(400, 'step', { text: 'Inspecting elements' });
    at(1400, 'step', { text: 'Adding property OutlineColor' });
    at(2600, 'step', { text: 'Binding stroke of the body to OutlineColor' });
    at(3600, 'step', { text: 'Validating the file' });
    const answer = 'Done — the outline colour is now a property you can bind in WinCC.';
    at(4400, 'message', { text: answer });
    at(4600, 'revision', {
      job_id: turn.jobId,
      file_url: null,
      png_url: null,
      params: [],
      render_metadata: null,
      diff_summary: ['Added property OutlineColor (color)', 'Bound stroke of “body” to OutlineColor'],
    });
    at(4800, 'done', { credits_charged: charged, changed: true, ...allowance(pack) });
    log.push({
      role: 'assistant', text: answer, job_id_after: turn.jobId, created_at: now,
    });
  }
  histories.set(turn.jobId, log);
  return stream;
}

export async function getEditChatTurn() {
  return { status: 'running', steps: [] };
}

export async function getEditChatHistory(jobId) {
  return { messages: (histories.get(jobId) || []).slice(), ...allowance(packFor(jobId)) };
}
